import { useEffect, useState, useRef, useMemo } from "react";
import { useQuery } from "react-query";
import { Resizable } from "re-resizable";
import { useParams } from "react-router-dom";
import { Container, Row, Col, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { getProject } from "@modules/Projects/services/projectCrud";
import { getTreesWithoutProject } from "@modules/Trees/services/treeCrud";
import { Weather } from "@modules/Weather";
import { ViewTypeButton } from "@modules/Projects/pages/ProjectDetail/Button";
import Breadcrumb from "@components/Breadcrumb/Breadcrumb";
import DataScienceProvider from "./context/DataScienceContext";
import GraphContainer from "./GraphContainer";
import SeriesChips from "./components/SeriesChips";
import {
  ReloadIcon,
  AngleLeftIcon,
  AngleRightIcon,
  FilterIconSolid,
  CalendarWeekIcon,
} from "@components/icons";
import Filter from "./variables/Filter";
import FilterBar from "./filters";
import "./DataScience.css";

function DataScience({ project, setProject }) {
  const { t } = useTranslation();
  const { pid } = useParams();
  const { mobile } = useSelector((state) => state.mobile);
  const dsTopOffset = "calc(var(--ts-topbar-height) + 0.5rem)";
  const dateSidebarWidth = mobile ? "min(88vw, 320px)" : "220px";
  const [height, setHeight] = useState(550);
  const [width, setWidth] = useState("100%");
  const [loadData, setLoadData] = useState(false);
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const [openFilters, setOpenFilters] = useState(true);
  const [openDateFilter, setOpenDateFilter] = useState(!mobile);
  const contentLeft = mobile ? "0" : openFilters ? "240px" : "0";
  const contentRight = mobile ? "0" : openDateFilter ? dateSidebarWidth : "0";
  const closedToggleTop = mobile
    ? `calc(var(--ts-topbar-height) + 2rem)`
    : `calc(var(--ts-topbar-height) + 1.5rem)`;
  const [navSidebarOffset, setNavSidebarOffset] = useState(0);
  const filterSidebarRef = useRef(null);
  const filterToggleRef = useRef(null);
  const filterBarRef = useRef(null);
  const dateSidebarRef = useRef(null);
  const dateToggleRef = useRef(null);

  // Check if navigation sidebars are open by checking DOM
  useEffect(() => {
    const checkNavSidebars = () => {
      const mainMenu = document.querySelector(".main-menu-sidebar.open");
      const projectSidebar = document.querySelector(".project-sidebar-dual.open");

      const hasOpenNavSidebar = !!(mainMenu || projectSidebar);

      if (hasOpenNavSidebar) {
        setNavSidebarOffset(240); // sidebar width
        // Close filter sidebar if navigation sidebar is opened
        if (openFilters) {
          setOpenFilters(false);
        }
      } else {
        setNavSidebarOffset(0);
      }
    };

    // Check initially
    checkNavSidebars();

    // Check periodically for sidebar changes
    const interval = setInterval(checkNavSidebars, 100);

    return () => clearInterval(interval);
  }, [openFilters]);

  // Close filters when clicking outside (but allow clicks on the toggle button)
  useEffect(() => {
    if (!openFilters) return;

    const handleClickOutside = (event) => {
      const insideSidebar = filterSidebarRef.current?.contains(event.target);
      const insideFilterBar = filterBarRef.current?.contains(event.target);
      const onToggleButton = filterToggleRef.current?.contains(event.target);

      if (!insideSidebar && !insideFilterBar && !onToggleButton) {
        setOpenFilters(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openFilters]);

  const isWithoutProject = pid === "without-project";

  // Fetch all trees when viewing "without-project" (with full installation data)
  const { data: allTrees, isLoading: isLoadingTrees } = useQuery(
    "trees-without-project-ds",
    getTreesWithoutProject,
    {
      enabled: isWithoutProject,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }
  );

  // Build pseudo-project for "without-project"
  const pseudoProject = useMemo(() => {
    if (!isWithoutProject || !allTrees) return null;

    // allTrees from getTreesWithoutProject already includes only trees without project
    // and already includes installations data

    // Group trees by area
    const areasMap = {};

    allTrees.forEach((tree) => {
      const areaId = tree.area_id || "without-location";
      const areaName = tree.area_name || t("without_location");

      if (!areasMap[areaId]) {
        areasMap[areaId] = {
          id: areaId,
          name: areaName,
          tree_set: [],
        };
      }

      areasMap[areaId].tree_set.push(tree);
    });

    const areas = Object.values(areasMap);

    return {
      id: "without-project",
      name: t("without_project"),
      areas: areas,
      weather: null,
    };
  }, [isWithoutProject, allTrees, t]);

  // Determine if we should fetch a regular project
  const shouldFetchRegularProject = !isWithoutProject && pid && pid !== "without-project";
  const shouldFetch = shouldFetchRegularProject && (!project || project.id !== +pid);

  // Only create the query if we're viewing a REAL project (not "without-project")
  const { data: fetchedProject } = useQuery(["ds_project", pid], () => getProject(pid), {
    // CRITICAL: Only enable if pid is definitely NOT "without-project"
    enabled: shouldFetchRegularProject && shouldFetch,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Set project when fetched
  useEffect(() => {
    if (shouldFetch && fetchedProject) {
      setProject?.(fetchedProject);
    }
  }, [fetchedProject, shouldFetch, setProject]);

  // Determine effective project (either fetched project or pseudo-project)
  const effectiveProject = isWithoutProject
    ? pseudoProject
    : shouldFetch
    ? fetchedProject
    : project;

  // Show loading spinner while loading
  if (isWithoutProject && (isLoadingTrees || !effectiveProject)) {
    return (
      <Container>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "80vh",
          }}
        >
          Loading without-project data...
        </div>
      </Container>
    );
  }

  // Don't render if no effective project
  if (!effectiveProject) {
    return (
      <Container>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "80vh",
          }}
        >
          Loading project data...
        </div>
      </Container>
    );
  }

  return (
    <DataScienceProvider>
      {/* Desktop: full breadcrumb with title, weather, view controls */}
      {!mobile && (
        <div
          className={`ds-header-safe-zone${openFilters ? " ds-header-safe-zone--left-open" : ""}${
            openDateFilter ? " ds-header-safe-zone--right-open" : ""
          }`}
        >
          <Breadcrumb
            title={
              <span className="d-inline-flex align-items-center gap-3 flex-wrap">
                <span>{effectiveProject?.name}</span>
                <Weather weather={effectiveProject?.weather} iconWidth={"1.2rem"} hideLabel />
              </span>
            }
            viewType={<ViewTypeButton id={pid} />}
          />
        </div>
      )}

      {/* Mobile: compact horizontal view switcher below topbar */}
      {mobile && (
        <div className="ds-mobile-view-bar">
          <ViewTypeButton id={pid} />
        </div>
      )}
      {/* Filter Sidebar - matches navigation sidebar style */}
      <div
        ref={filterSidebarRef}
        className="ds-filter-sidebar"
        style={{
          position: "fixed",
          left: 0,
          top: dsTopOffset,
          bottom: 0,
          width: "240px",
          background: "var(--color-bg-surface, #fff)",
          borderRight: "1px solid var(--color-border-subtle, #dee2e6)",
          paddingLeft: "0.75rem",
          boxSizing: "border-box",
          zIndex: 20,
          overflowY: "auto",
          transition: "transform 0.3s ease",
          transform: openFilters ? "translateX(0)" : "translateX(-100%)",
        }}
      >
        <div ref={filterBarRef}>
          <FilterBar project={effectiveProject} setOpenFilters={setOpenFilters} />
        </div>
      </div>

      {/* Collapse button for filter sidebar */}
      {openFilters && (
        <button
          className="sidebar-collapse-btn ds-sidebar-collapse-btn-left"
          onClick={() => setOpenFilters(false)}
          title={t("close")}
        >
          <AngleLeftIcon size={20} />
        </button>
      )}

      {/* Open button when filters are closed */}
      {!openFilters && (
        <button
          ref={filterToggleRef}
          onClick={() => {
            // Close any open navigation sidebars first
            const mainMenuSidebar = document.querySelector(".main-menu-sidebar.open");
            const projectSidebar = document.querySelector(".project-sidebar-dual.open");

            // Find the toggle buttons
            const toggleButtons = document.querySelectorAll(".sidebar-toggle-btn");

            // Only click toggle button if its corresponding sidebar is open
            if (mainMenuSidebar && toggleButtons[0]) {
              toggleButtons[0].click(); // First button is menu
            }
            if (projectSidebar && toggleButtons[1]) {
              toggleButtons[1].click(); // Second button is projects
            }

            setOpenFilters(true);
          }}
          title={t("filters")}
          style={{
            position: "fixed",
            top: closedToggleTop,
            left: `calc(${navSidebarOffset}px + 1rem)`,
            zIndex: 1051,
            background: "var(--bs-primary, #0d6efd)",
            color: "#fff",
            border: "none",
            borderRadius: "0.375rem",
            padding: "0.5rem 0.75rem",
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            transition: "left 0.3s ease",
          }}
        >
          <FilterIconSolid />
          {!mobile && <span>{t("filters")}</span>}
          <AngleRightIcon size={20} />
        </button>
      )}

      <Container
        fluid
        className={`ds ds-content-safe-zone${
          openFilters ? " ds-content-safe-zone--left-open" : ""
        }${openDateFilter ? " ds-content-safe-zone--right-open" : ""}`}
        style={{
          position: "relative",
          marginLeft: contentLeft,
          paddingRight: openDateFilter ? contentRight : undefined,
          transition: "margin-left 0.3s ease, padding-right 0.3s ease",
          marginTop: "0",
        }}
      >
        <Row className="g-0">
          <Col lg={12} style={{ position: "relative" }}>
            {/* <Variables /> */}
            <Row className={"g-0"}>
              <Col lg={12}>
                <Resizable
                  size={{ width, height }}
                  onResizeStop={(e, direction, ref, d) => {
                    setWidth(width + d.width);
                    setHeight(height + d.height);
                  }}
                  minHeight={300}
                  enable={{
                    top: false,
                    right: false,
                    bottom: true,
                    left: false,
                    topRight: false,
                    bottomRight: false,
                    bottomLeft: false,
                    topLeft: false,
                  }}
                  maxHeight={"70vh"}
                  handleClasses="handlebar"
                  handleComponent={<>ok</>}
                  handleStyles={{
                    bottom: {
                      marginTop: -7,
                      marginLeft: -5,
                      top: "0%",
                      left: "0%",
                      width: 0,
                      height: 0,
                      boxSizing: "border-box",
                      zIndex: 1,
                      background: "#ccc",
                      borderRadius: "4px",
                    },
                  }}
                >
                  <div style={{ position: "relative", height: "100%" }}>
                    <div
                      style={{
                        position: "absolute",
                        top: "8px",
                        right: mobile ? "10px" : "60px",
                        zIndex: 10,
                        display: "flex",
                        gap: "0.5rem",
                      }}
                    >
                      <SeriesChips />
                    </div>
                    <GraphContainer
                      loadData={loadData}
                      setLoadData={setLoadData}
                      setShouldRefetch={setShouldRefetch}
                      shouldRefetch={shouldRefetch}
                    />
                  </div>
                  <div ref={dateSidebarRef}>
                    <Filter
                      openDateFilter={openDateFilter}
                      setOpenDateFilter={setOpenDateFilter}
                      topOffset={dsTopOffset}
                    />
                  </div>

                  {/* Date Filter Toggle Buttons */}
                  {openDateFilter && (
                    <button
                      className="sidebar-collapse-btn sidebar-collapse-btn--right ds-sidebar-collapse-btn-right"
                      onClick={() => setOpenDateFilter(false)}
                      title={t("close_time_controls")}
                      style={{ right: dateSidebarWidth, left: "auto" }}
                    >
                      <AngleRightIcon size={20} />
                    </button>
                  )}

                  {!openDateFilter && (
                    <button
                      ref={dateToggleRef}
                      onClick={() => setOpenDateFilter(true)}
                      title={t("date", "Date")}
                      style={{
                        position: "fixed",
                        top: closedToggleTop,
                        right: "1rem",
                        zIndex: 1051,
                        background: "var(--bs-primary, #0d6efd)",
                        color: "#fff",
                        border: "none",
                        borderRadius: "0.375rem",
                        padding: "0.5rem 0.75rem",
                        cursor: "pointer",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      {mobile ? (
                        <>
                          <AngleLeftIcon size={20} />
                          <CalendarWeekIcon />
                        </>
                      ) : (
                        <>
                          <AngleLeftIcon size={20} />
                          <span>{t("date", "Date")}</span>
                        </>
                      )}
                    </button>
                  )}

                  {/* 👇 Overlay button */}
                  {shouldRefetch && (
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 1000,
                      }}
                    >
                      <Button
                        variant="primary"
                        onClick={() => {
                          setLoadData(true);
                        }}
                      >
                        <ReloadIcon /> {t("load_data")}
                      </Button>
                    </div>
                  )}
                </Resizable>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </DataScienceProvider>
  );
}

export default DataScience;
